import { useState } from 'react';
import { X, GitFork, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';

/**
 * Modal to display a visual fork tree for a root chat and all its descendants
 */
export default function ForkTreeModal({
  isOpen,
  onClose,
  chatTree, // The root chat node with children
  onNavigateToChat
}) {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  if (!isOpen || !chatTree) return null;

  const toggleNode = (chatId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      return next;
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const renderTreeNode = (node, depth = 0, isLast = true, parentLines = []) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id) || depth === 0; // Root always expanded

    return (
      <div key={node.id}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 'var(--space-2) var(--space-3)',
            marginLeft: depth > 0 ? `${depth * 24}px` : 0,
            position: 'relative'
          }}
        >
          {/* Tree lines */}
          {depth > 0 && (
            <div style={{
              position: 'absolute',
              left: `${(depth - 1) * 24 + 12}px`,
              top: 0,
              bottom: isLast ? '50%' : 0,
              width: '1px',
              backgroundColor: 'var(--color-border)'
            }} />
          )}
          {depth > 0 && (
            <div style={{
              position: 'absolute',
              left: `${(depth - 1) * 24 + 12}px`,
              top: '50%',
              width: '12px',
              height: '1px',
              backgroundColor: 'var(--color-border)'
            }} />
          )}

          {/* Expand/collapse toggle */}
          {hasChildren ? (
            <button
              onClick={() => toggleNode(node.id)}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--color-surface-secondary)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 'var(--space-2)',
                flexShrink: 0
              }}
            >
              {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : (
            <div style={{ width: '20px', marginRight: 'var(--space-2)', flexShrink: 0 }}>
              <GitFork size={14} style={{ color: 'var(--color-text-tertiary)' }} />
            </div>
          )}

          {/* Node content */}
          <div
            onClick={() => onNavigateToChat(node.id)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: depth === 0 ? 'var(--primary-50)' : 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary-400)';
              e.currentTarget.style.backgroundColor = depth === 0 ? 'var(--primary-100)' : 'var(--primary-50)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.backgroundColor = depth === 0 ? 'var(--primary-50)' : 'var(--color-surface)';
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontWeight: depth === 0 ? 600 : 500,
                color: 'var(--color-text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem'
              }}>
                {depth === 0 && (
                  <span style={{
                    display: 'inline-block',
                    padding: '1px 4px',
                    backgroundColor: 'var(--primary-600)',
                    color: 'white',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    marginRight: 'var(--space-2)'
                  }}>
                    ROOT
                  </span>
                )}
                {node.title || 'Untitled'}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: 'var(--color-text-tertiary)',
                marginTop: '2px'
              }}>
                {depth > 0 && `Forked at message ${(node.forkedAtMessageIndex ?? 0) + 1} • `}
                {formatDate(node.createdAt)}
                {node.messages && ` • ${node.messages.length} msgs`}
              </div>
            </div>
            <ExternalLink size={14} style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }} />
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child, index) =>
              renderTreeNode(child, depth + 1, index === node.children.length - 1, [...parentLines, !isLast])
            )}
          </div>
        )}
      </div>
    );
  };

  // Count total forks recursively
  const countForks = (node) => {
    if (!node.children || node.children.length === 0) return 0;
    return node.children.length + node.children.reduce((sum, child) => sum + countForks(child), 0);
  };

  const totalForks = countForks(chatTree);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 'var(--space-4)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-4)',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <GitFork size={20} />
              Fork Tree
            </h2>
            <p style={{
              margin: 'var(--space-1) 0 0',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)'
            }}>
              {totalForks} fork{totalForks !== 1 ? 's' : ''} from this conversation
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--color-surface-secondary)',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tree content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--space-3)'
        }}>
          {renderTreeNode(chatTree)}
        </div>

        {/* Footer */}
        <div style={{
          padding: 'var(--space-3) var(--space-4)',
          borderTop: '1px solid var(--color-border)',
          fontSize: '0.75rem',
          color: 'var(--color-text-tertiary)',
          textAlign: 'center'
        }}>
          Click on any chat to open it
        </div>
      </div>
    </div>
  );
}
